
import { computed, defineComponent, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { message as antMessage } from 'ant-design-vue'
import newChatApi from '@/api/newChat'
import { renderQrCode } from '@/utils/qrcode'
import type { AdviserInfo } from './types'
import { getOneToOneAvatar, getOneToOneNickname } from './chatUser'

const isFdImOk = (res: any) => {
  if (!res) return false
  const msg = String(res.message ?? res.msg ?? '').trim()
  if (msg === '操作失败' || msg === 'fail' || msg === 'failed') return false
  return (
    res.code === 0 ||
    res.code === 1 ||
    msg === 'success' ||
    msg === '操作成功' ||
    res.info === 'success'
  )
}

export default defineComponent({
  name: 'ChatWhatsApp',
  props: {
    customerUid: {
      type: [String, Number],
      default: '',
    },
    adviserName: {
      type: String,
      default: '',
    },
    adviserTitle: {
      type: String,
      default: '',
    },
  },
  emits: ['adviser-info'],
  setup(props, { emit }) {
    const route = useRoute()
    const waLinkData = ref<any>(null)
    const qrRef = ref<HTMLElement | null>(null)
    const showDialog = ref(false)
    const fdLogo = require('@/assets/deepLoadingFd.png')

    const ukey = computed(() => (route.query.token as string) || '')

    const chatHeaders = computed(() => {
      const token = route.query.token as string
      return token ? { token } : {}
    })

    const waLinkUrl = computed(() => {
      const data = waLinkData.value || {}
      return data.assigneeWsLink || data.wechatLink || ''
    })

    const assigneeWsPhone = computed(() => {
      const phone = waLinkData.value?.assigneeWsPhone
      return phone ? String(phone) : ''
    })

    const displayAdviserName = computed(() => {
      const data = waLinkData.value || {}
      return getOneToOneNickname(data) || props.adviserName || '1v1服务'
    })

    const resolveCustomerSuid = async (): Promise<string> => {
      const routeSuid = route.query.suid as string
      if (routeSuid) return routeSuid

      const res: any = await newChatApi.getImAccount({
        data: { ukey: ukey.value },
        headers: chatHeaders.value,
      })
      if (!isFdImOk(res)) {
        throw new Error(res?.message || '获取用户信息失败')
      }
      const data = res.data || {}
      return String(data.uid || data.suid || ukey.value)
    }

    const fetchWaLink = async () => {
      if (!ukey.value) return

      try {
        const suid = String(props.customerUid || await resolveCustomerSuid())
        const res: any = await newChatApi.getWaLink({
          data: { suid },
          headers: chatHeaders.value,
        })
        if (!isFdImOk(res)) {
          console.warn('getWaLink failed:', res?.message)
          return
        }

        waLinkData.value = res.data || {}
        const adviserName = getOneToOneNickname(waLinkData.value)
        const adviserAvatar = getOneToOneAvatar(waLinkData.value)
        if (adviserName || adviserAvatar) {
          emit('adviser-info', {
            empNickname: adviserName,
            empAvatar: adviserAvatar,
            nickname: adviserName,
            user_name: adviserName,
            head_portrait: adviserAvatar,
          } as AdviserInfo)
        }
      } catch (error) {
        console.error('Failed to get WhatsApp link', error)
      }
    }

    const renderQr = async () => {
      await nextTick()
      if (qrRef.value && waLinkUrl.value) {
        renderQrCode(qrRef.value, waLinkUrl.value, 200)
      }
    }

    const copyPhone = async () => {
      const phone = assigneeWsPhone.value
      if (!phone) return
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(phone)
        } else {
          const input = document.createElement('textarea')
          input.value = phone
          input.style.position = 'fixed'
          input.style.opacity = '0'
          document.body.appendChild(input)
          input.select()
          document.execCommand('copy')
          document.body.removeChild(input)
        }
        antMessage.success('复制成功')
      } catch (err) {
        console.error('copy phone failed', err)
        antMessage.error('复制失败')
      }
    }

    const openDialog = async () => {
      showDialog.value = true
      await renderQr()
    }

    const closeDialog = () => {
      showDialog.value = false
    }

    onMounted(() => {
      fetchWaLink()
    })

    watch(
      () => props.customerUid,
      (uid) => {
        if (uid) fetchWaLink()
      }
    )

    return {
      waLinkUrl,
      assigneeWsPhone,
      displayAdviserName,
      showDialog,
      qrRef,
      fdLogo,
      openDialog,
      closeDialog,
      copyPhone,
    }
  },
})
